import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Editor } from '@tiptap/core';
import dayjs from 'dayjs';
import { StatusCodes } from 'http-status-codes';
import _ from 'lodash';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { unstable_useBlocker as useBlocker } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import InformedIndividualSectionFactory from './informedIndividualsSection/InformedIndividualSectionFactory';

import { axiosInstanceForTenantAPI } from '../../../../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../../../../assets/utils/helpers';
import { ReportTypeProps } from '../../../../../components/customer/report/types';
import { useToastNotificationContext } from '../../../../../hooks/global/alert/useToastNotification';
import { ActiveMarks } from '../../../../../interfaces/ActiveMarks';
import { DynamicField } from '../../../../../interfaces/DynamicField';
import { DynamicFieldResponse } from '../../../../../interfaces/DynamicFieldResponse';
import { FinalReportData } from '../../../../../interfaces/FinalReportData';
import { FinalReportTemplate } from '../../../../../interfaces/FinalReportTemplate';
import { PrepopulatePermissions } from '../../../../../interfaces/PrepopulatePermissions';
import { setReport } from '../../../../../slices/reportDataSlice';
import { RootState, useTypedSelector } from '../../../../../utils/store';
import { fieldTypes } from '../../../settings/finalReport/templateDialogs/common/fieldTypes';
import ConfirmationDialog from '../common/ConfirmationDialog';
import CustomFields from '../common/CustomFields';
import DefaultFields from '../common/DefaultFields';
import DeleteWarningDialog from '../common/DeleteWarningDialog';
import ReportToolbar from '../common/ReportToolbar';
import SuccessDialog from '../common/SuccessDialog';
import UnsavedChangesWarningDialog from '../common/UnsavedChangesDialog';

import DownloadFinalReportButton from '~/components/common/finalReport/downloadReportButton';
import { Button as NewButton } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import getStatusOptions from '~/constants/statusOptions';
import { FinalReportDocumentField } from '~/interfaces/FinalReportDocumentField';
import {
  InformedIndividualItemType,
  ModifiedFinalReportDocument,
} from '~/interfaces/ModifiedFinalReportDocument';

interface DynamicReportDialogProps {
  open: boolean;
  existingTemplate: FinalReportTemplate;
  reportTypeList: ReportTypeProps[];
  draftValues: FinalReportData | null;
  reportID: string;
  onClose: () => void;
  onMinimize: () => void;
  onDelete: () => void;
  setExisting: React.Dispatch<React.SetStateAction<FinalReportData | null>>;
}

export interface DefaultFieldValues {
  reportType: string;
  referenceID: string;
  assignee: string;
  priority: number;
  dateReceived: string | null;
}

export interface DefaultFieldErrors {
  reportType: string;
  referenceID: string;
  assignee: string;
  priority: string;
  dateReceived: string;
}

interface LooseObject {
  //ESlint needs to be ignored for this interface due to the requirements of dynamic object assignment for final report data submission
  [key: string]: any; // eslint-disable-line
}

const getPriorityString = (priority: number) => {
  switch (priority) {
    case 0:
      return 'Low';
    case 1:
      return 'Medium';
    case 2:
      return 'High';
    case 3:
      return 'Critical';
    default:
      return '';
  }
};

const DynamicReportDialog = ({
  open,
  draftValues,
  existingTemplate,
  reportTypeList,
  reportID,
  setExisting,
  onClose,
  onMinimize,
  onDelete,
}: DynamicReportDialogProps) => {
  const REPORT_URL = '/final-reports';
  const STATUS_FINAL_REPORT_DRAFT = 0;
  const STATUS_FINAL_REPORT_CLOSED = 1;
  const STATUS_REPORT_CLOSED_RESOLVED = 3;

  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const dispatch = useDispatch();
  const reportDetails = useSelector(
    (state: RootState) => state.reportData.report,
  );

  const getPriorityNumber = (priority: string) => {
    switch (priority) {
      case 'Low':
        return 0;
      case 'Medium':
        return 1;
      case 'High':
        return 2;
      case 'Critical':
        return 3;
      default:
        return -1;
    }
  };

  const permissions: PrepopulatePermissions = {
    caseManager: existingTemplate.case_manager_name,
    dateReceived: existingTemplate.date_received,
    reportType: existingTemplate.report_type,
    reportReference: existingTemplate.report_reference,
    reportSeverity: existingTemplate.report_severity,
  };

  const [hasInformedIndividuals, setHasInformedIndividuals] =
    useState<boolean>(true);

  const [previousDefaultFieldValues] = useState<DefaultFieldValues>(() => {
    if (draftValues) {
      return {
        reportType: permissions.reportType ? draftValues.report_type : '',
        referenceID: permissions.reportReference
          ? reportDetails.formattedReportNumber
          : '',
        assignee: permissions.caseManager ? draftValues.case_manager_name : '',
        priority: permissions.reportSeverity
          ? getPriorityNumber(draftValues.report_severity)
          : -1,
        dateReceived: permissions.dateReceived
          ? dayjs(draftValues.date_received).format('YYYY-MM-DD')
          : '',
      };
    }
    return {
      reportType: permissions.reportType ? reportDetails.reportType.name : '',
      referenceID: permissions.reportReference
        ? reportDetails.formattedReportNumber
        : '',
      assignee: permissions.caseManager ? reportDetails.assigneeName : '',
      priority: permissions.reportSeverity ? reportDetails.priority : -1,
      dateReceived: permissions.dateReceived
        ? dayjs(reportDetails.createdAt).format('YYYY-MM-DD')
        : '',
    };
  });

  const draftCustomFieldValueResponse: DynamicFieldResponse[] | undefined =
    draftValues?.final_report_custom_field_values.map((field) => {
      const siblingFields: DynamicFieldResponse[] = field.siblings.map(
        (sibling) => {
          return {
            id: sibling.id,
            placeholder: sibling.final_report_custom_field_sibling.placeholder,
            label: sibling.final_report_custom_field_sibling.label,
            dropdown_choices:
              sibling.final_report_custom_field_sibling.dropdown_choices,
            is_mandatory:
              sibling.final_report_custom_field_sibling.is_mandatory,
            type: sibling.final_report_custom_field_sibling.type,
            value: sibling.value,
          };
        },
      );

      return {
        id: field.id,
        placeholder: field.final_report_custom_field.placeholder,
        label: field.final_report_custom_field.label,
        dropdown_choices: field.final_report_custom_field.dropdown_choices,
        is_mandatory: field.final_report_custom_field.is_mandatory,
        type: field.final_report_custom_field.type,
        value: field.value,
        siblings: siblingFields,
      };
    });

  const informedIndividualsItems = useTypedSelector(
    (state) => state.informedIndividuals.items,
  );
  const reportStatus = useTypedSelector(
    (state) => state.reportData.report.status,
  );
  const caseID = useTypedSelector(
    (state) => state.reportData.report.referenceId,
  );
  const departments = useTypedSelector((state) => state.reports.departments);

  const statusOptions = getStatusOptions();

  const getDepartmentName = (did: number): string => {
    let departmentName = '';
    const findDepartment = _.find(departments, (e) => e.value == did);
    if (typeof findDepartment != 'undefined')
      departmentName = findDepartment.label;

    return departmentName;
  };

  const modifiedItems: Array<InformedIndividualItemType> =
    informedIndividualsItems.map((item) => {
      return {
        id: item.id,
        report: item.report,
        name: item.name,
        associatedDepartment: getDepartmentName(item.associated_department),
        notificationReason: item.notification_reason,
        dateInformed: dayjs(item.date_informed_at).format('LL'),
      };
    });

  const draftCustomFieldValues = draftCustomFieldValueResponse?.map((field) => {
    const fieldSiblings = field.siblings?.map((sibling) => {
      return {
        id: sibling.id,
        fieldId: uuidv4(),
        type: sibling.type,
        value: sibling.value ?? '',
        fieldTitle: sibling.label,
        placeholder: sibling.placeholder,
        isMandatory: sibling.is_mandatory,
        dropdownItems: sibling.dropdown_choices,
      };
    });

    const parentField: DynamicField = {
      id: field.id,
      fieldId: uuidv4(),
      type: field.type,
      value: field.value ?? '',
      fieldTitle: field.label,
      placeholder: field.placeholder,
      isMandatory: field.is_mandatory,
      dropdownItems: field.dropdown_choices,
      siblings: fieldSiblings,
    };

    return parentField;
  });

  const [previousCustomFieldValues, setPreviousCustom] = useState<
    DynamicField[]
  >(() => {
    if (draftCustomFieldValues) {
      return [...draftCustomFieldValues];
    }
    const startValues = existingTemplate.final_report_custom_field.map(
      (field) => {
        const fieldSiblings = field.siblings?.map((sibling) => {
          return {
            id: sibling.id,
            fieldId: uuidv4(),
            type: sibling.type,
            value: sibling.placeholder ?? '',
            fieldTitle: sibling.label,
            placeholder: sibling.placeholder,
            isMandatory: sibling.is_mandatory,
            dropdownItems: sibling.dropdown_choices,
          };
        });

        const parentField: DynamicField = {
          id: field.id,
          fieldId: uuidv4(),
          type: field.type,
          value: field.placeholder ?? '',
          fieldTitle: field.label,
          placeholder: field.placeholder,
          isMandatory: field.is_mandatory,
          dropdownItems: field.dropdown_choices,
          siblings: fieldSiblings,
        };

        return parentField;
      },
    );
    return startValues;
  });

  const [defaultFieldValues, setDefaultFieldValues] =
    useState<DefaultFieldValues>({
      ...previousDefaultFieldValues,
    });

  const [customFieldValues, setCustomValues] = useState<DynamicField[]>(
    _.cloneDeep(previousCustomFieldValues),
  );

  const defaultFieldValueRef = useRef<DefaultFieldValues>({
    ...defaultFieldValues,
  });
  const customFieldValueRef = useRef<DynamicField[]>(
    _.cloneDeep(customFieldValues),
  );

  const [editor, setEditor] = useState<Editor | null>(null);
  const [activeMarks, setMarks] = useState<ActiveMarks>({
    bold: false,
    italic: false,
    underline: false,
    bulletList: false,
    numberedList: false,
    heading: false,
    textStyle: false,
  });

  const [openConfirmation, setOpen] = useState(false);
  const [openDeleteWarning, setOpenWarning] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [errorMessage, setError] = useState('');
  const [infoMessage, setInfo] = useState('');

  const localizedRequired = intl.formatMessage({
    defaultMessage: 'Required',
  });

  // Data Handling and Manipulation Methods

  const validateMandatoryFields = () => {
    let hasErrors = false;
    const validatingFields = [...customFieldValueRef.current];
    validatingFields.forEach((field) => {
      if (
        field.type === fieldTypes.title ||
        field.type === fieldTypes.descriptionText
      ) {
        return;
      }
      field.siblings?.forEach((sibling) => {
        if (sibling.isMandatory) {
          if (!sibling.value) {
            hasErrors = true;
            sibling.errorMessage = localizedRequired;
          } else {
            sibling.errorMessage = undefined;
          }
        }
      });
      if (field.isMandatory) {
        if (field.type === fieldTypes.bigTextField) {
          const extractedText = field.value.replace(/<[^>]*>/g, '');
          if (!extractedText) {
            hasErrors = true;
            field.errorMessage = localizedRequired;
          } else {
            field.errorMessage = undefined;
          }
          return;
        }
        if (field.type === fieldTypes.datePicker) {
          if (field.value === 'Invalid Date' || field.value === '') {
            hasErrors = true;
            field.errorMessage = intl.formatMessage({
              defaultMessage: 'Missing or Invalid Date',
            });
          } else {
            field.errorMessage = undefined;
          }
          return;
        }
        if (!field.value) {
          hasErrors = true;
          field.errorMessage = localizedRequired;
        } else {
          field.errorMessage = undefined;
        }
      }
    });
    setCustomValues(validatingFields);
    return hasErrors;
  };

  const processFields = () => {
    const requestCustomFieldValues = customFieldValueRef.current.map(
      (field) => {
        const siblingFieldValues = field.siblings?.map((siblingField) => {
          if (draftValues) {
            return {
              id: siblingField.id!,
              value: siblingField.value,
            };
          }
          return {
            final_report_custom_field_sibling: siblingField.id!,
            value: siblingField.value,
          };
        });
        if (draftValues) {
          return {
            id: field.id!,
            value: field.value,
            siblings: siblingFieldValues,
          };
        }
        return {
          final_report_custom_field: field.id!,
          value: field.value,
          siblings: siblingFieldValues,
        };
      },
    );
    const requestBody: LooseObject = {
      final_report_template: existingTemplate.id!,
      report: reportID,
      title: intl.formatMessage(
        { defaultMessage: 'New Report from {templateName}' },
        { templateName: existingTemplate.template_name },
      ),
      final_report_custom_field_values: requestCustomFieldValues,
    };

    return requestBody;
  };

  const preventUnsavedReload = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault();
  }, []);

  const checkUnsavedChanges = useCallback(() => {
    let hasUnsavedChanges = false;
    hasUnsavedChanges = !_.isEqual(
      previousDefaultFieldValues,
      defaultFieldValueRef.current,
    );
    customFieldValueRef.current.forEach((field, rootIndex) => {
      if (
        field.type === fieldTypes.title ||
        field.type === fieldTypes.descriptionText
      ) {
        return;
      }
      field.siblings?.forEach((sibling, siblingIndex) => {
        if (!previousCustomFieldValues[rootIndex].siblings!) {
          return;
        }
        if (
          sibling.value !==
          previousCustomFieldValues[rootIndex].siblings![siblingIndex].value
        ) {
          hasUnsavedChanges = true;
        }
      });
      if (field.value !== previousCustomFieldValues[rootIndex].value) {
        hasUnsavedChanges = true;
      }
    });
    return hasUnsavedChanges;
  }, [previousCustomFieldValues, previousDefaultFieldValues]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const hasChanges = checkUnsavedChanges();
    return hasChanges && currentLocation.pathname !== nextLocation.pathname;
  });

  //Event Handlers

  const handleChangeEditor = useCallback((newEditor: Editor | null) => {
    setEditor(newEditor);
  }, []);

  const handleChangeMarks = useCallback((newMarks: ActiveMarks) => {
    setMarks(newMarks);
  }, []);

  const handleClose = () => {
    onClose();
  };

  const handleOpenConfirmation = () => {
    const hasMandatoryErrors = validateMandatoryFields();
    if (hasMandatoryErrors) {
      return;
    }
    setOpen(true);
  };
  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  const handleOpenSuccess = () => {
    setOpenSuccess(true);
  };
  const handleCloseSuccess = () => {
    setOpenSuccess(false);
  };

  const handleOpenWarning = () => {
    setOpenWarning(true);
  };
  const handleCloseWarning = () => {
    setOpenWarning(false);
  };

  const handleCloseSubmission = () => {
    handleCloseSuccess();
    handleClose();
  };

  const handleDeleteDraftSubmission = () => {
    handleCloseWarning();
    onDelete();
    handleClose();
  };

  const handleDiscardChanges = () => {
    setCustomValues(_.cloneDeep(previousCustomFieldValues));
    customFieldValueRef.current = _.cloneDeep(previousCustomFieldValues);
    setDefaultFieldValues({ ...previousDefaultFieldValues });
    defaultFieldValueRef.current = { ...previousDefaultFieldValues };
    handleClose();
  };

  const handleUpdateExistingChanges = (data: FinalReportData) => {
    const responseCustomFieldValueResponse: DynamicFieldResponse[] | undefined =
      data.final_report_custom_field_values.map((field) => {
        const siblingFields: DynamicFieldResponse[] = field.siblings.map(
          (sibling) => {
            return {
              id: sibling.id,
              placeholder:
                sibling.final_report_custom_field_sibling.placeholder,
              label: sibling.final_report_custom_field_sibling.label,
              dropdown_choices:
                sibling.final_report_custom_field_sibling.dropdown_choices,
              is_mandatory:
                sibling.final_report_custom_field_sibling.is_mandatory,
              type: sibling.final_report_custom_field_sibling.type,
              value: sibling.value,
            };
          },
        );

        return {
          id: field.id,
          placeholder: field.final_report_custom_field.placeholder,
          label: field.final_report_custom_field.label,
          dropdown_choices: field.final_report_custom_field.dropdown_choices,
          is_mandatory: field.final_report_custom_field.is_mandatory,
          type: field.final_report_custom_field.type,
          value: field.value,
          siblings: siblingFields,
        };
      });

    const responseCustomFieldValues = responseCustomFieldValueResponse.map(
      (field) => {
        const fieldSiblings = field.siblings?.map((sibling) => {
          return {
            id: sibling.id,
            fieldId: uuidv4(),
            type: sibling.type,
            value: sibling.value ?? '',
            fieldTitle: sibling.label,
            placeholder: sibling.placeholder,
            isMandatory: sibling.is_mandatory,
            dropdownItems: sibling.dropdown_choices,
          };
        });

        const parentField: DynamicField = {
          id: field.id,
          fieldId: uuidv4(),
          type: field.type,
          value: field.value ?? '',
          fieldTitle: field.label,
          placeholder: field.placeholder,
          isMandatory: field.is_mandatory,
          dropdownItems: field.dropdown_choices,
          siblings: fieldSiblings,
        };

        return parentField;
      },
    );

    setPreviousCustom(responseCustomFieldValues);
    setCustomValues(responseCustomFieldValues);
    customFieldValueRef.current = responseCustomFieldValues;
  };

  const handleSuccessfulUpdate = (data: FinalReportData) => {
    setExisting(data);
    setSaving(false);
    handleUpdateExistingChanges(data);
    setInfo(intl.formatMessage({ defaultMessage: 'Changes saved.' }));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    const requestBody = processFields();
    requestBody.status = STATUS_FINAL_REPORT_DRAFT;
    try {
      if (draftValues) {
        requestBody.id = draftValues.id!;
        delete requestBody.report;
        const response =
          await axiosInstanceForTenantAPI().patch<FinalReportData>(
            REPORT_URL + `/${draftValues.id!}`,
            requestBody,
          );
        if (response.status === StatusCodes.OK) {
          handleSuccessfulUpdate(response.data);
        }
      } else {
        const response =
          await axiosInstanceForTenantAPI().post<FinalReportData>(
            REPORT_URL,
            requestBody,
          );
        if (response.status === StatusCodes.CREATED) {
          handleSuccessfulUpdate(response.data);
        }
      }
    } catch (err: unknown) {
      let errorResponse = intl.formatMessage({
        // eslint-disable-next-line quotes
        defaultMessage: "Error happened. We're currently fixing it.",
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorResponse = intl.formatMessage({
            // eslint-disable-next-line quotes
            defaultMessage: "Error happened. We're currently fixing it.",
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
        ) {
          errorResponse = intl.formatMessage({
            defaultMessage: 'Failure to process request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
        ) {
          errorResponse = intl.formatMessage({
            defaultMessage: 'You are unauthorized to update report',
          });
        } else if (`${err.response.status}` === String(StatusCodes.FORBIDDEN)) {
          errorResponse = '';
          createToastNotification(
            intl.formatMessage({
              defaultMessage:
                'You cannot use this request. Make sure the report is not closed or archived.',
            }),
            'error',
          );
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorResponse = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      setError(errorResponse);
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    handleCloseConfirmation();
    setError('');
    setSubmitting(true);
    const requestBody = processFields();
    requestBody.status = STATUS_FINAL_REPORT_CLOSED;

    //check if it has informed individuals
    requestBody.has_informed_individuals = hasInformedIndividuals;

    try {
      if (draftValues) {
        requestBody.id = draftValues.id!;
        delete requestBody.report;

        const response =
          await axiosInstanceForTenantAPI().patch<FinalReportData>(
            REPORT_URL + `/${draftValues.id!}`,
            requestBody,
          );
        if (response.status === StatusCodes.OK) {
          setExisting(response.data);
          handleUpdateExistingChanges(response.data);
          setSubmitting(false);
          handleOpenSuccess();
          window.removeEventListener('beforeunload', preventUnsavedReload);
          dispatch(
            setReport({
              ...reportDetails,
              status: STATUS_REPORT_CLOSED_RESOLVED,
            }),
          );
        }
      } else {
        const response =
          await axiosInstanceForTenantAPI().post<FinalReportData>(
            REPORT_URL,
            requestBody,
          );
        if (response.status === StatusCodes.CREATED) {
          setExisting(response.data);
          handleUpdateExistingChanges(response.data);
          setSubmitting(false);
          handleOpenSuccess();
          window.removeEventListener('beforeunload', preventUnsavedReload);
          dispatch(
            setReport({
              ...reportDetails,
              status: STATUS_REPORT_CLOSED_RESOLVED,
            }),
          );
        }
      }
    } catch (err: unknown) {
      let errorResponse = intl.formatMessage({
        // eslint-disable-next-line quotes
        defaultMessage: "Error happened. We're currently fixing it.",
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorResponse = intl.formatMessage({
            // eslint-disable-next-line quotes
            defaultMessage: "Error happened. We're currently fixing it.",
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
        ) {
          errorResponse = intl.formatMessage({
            defaultMessage: 'Failure to process request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
        ) {
          errorResponse = intl.formatMessage({
            defaultMessage: 'You are unauthorized to update report',
          });
        } else if (`${err.response.status}` === String(StatusCodes.FORBIDDEN)) {
          errorResponse = '';
          createToastNotification(
            intl.formatMessage({
              defaultMessage:
                'You cannot use this request. Make sure the report is not closed or archived.',
            }),
            'error',
          );
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorResponse = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      setError(errorResponse);
      setSubmitting(false);
    }
  };

  const handleUnsavedChanges = useCallback(() => {
    const unsavedChanges = checkUnsavedChanges();
    if (unsavedChanges) {
      window.addEventListener('beforeunload', preventUnsavedReload);
    } else {
      window.removeEventListener('beforeunload', preventUnsavedReload);
    }
  }, [checkUnsavedChanges, preventUnsavedReload]);

  const handleCloseUnsavedWarning = () => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleProceedUnsavedWarning = () => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  useEffect(() => {
    handleUnsavedChanges();
    return () => {
      window.removeEventListener('beforeunload', preventUnsavedReload);
    };
  }, [preventUnsavedReload, handleUnsavedChanges]);

  const CustomFieldSection = useMemo(() => {
    return (
      <CustomFields
        checkChanges={handleUnsavedChanges}
        valueRef={customFieldValueRef}
        fields={customFieldValues}
        setValues={setCustomValues}
        setEditor={handleChangeEditor}
        setMarks={handleChangeMarks}
      />
    );
  }, [
    customFieldValues,
    handleChangeEditor,
    handleChangeMarks,
    handleUnsavedChanges,
  ]);

  const pdfFields: FinalReportDocumentField[] = customFieldValueRef.current.map(
    (field) => {
      const siblingFields: FinalReportDocumentField[] = field.siblings
        ? field.siblings.map((sibling) => {
            return {
              title: sibling.fieldTitle,
              type: sibling.type,
              value: sibling.value || '',
            };
          })
        : [];

      return {
        title: field.fieldTitle,
        type: field.type,
        value: field.value || '',
        siblings: siblingFields,
      };
    },
  );

  const pdfData: ModifiedFinalReportDocument = {
    title: existingTemplate.template_title,
    status: statusOptions[reportStatus]?.name,
    reportType: defaultFieldValues.reportType,
    referenceID: defaultFieldValues.referenceID,
    assignee: defaultFieldValues.assignee,
    priority: getPriorityString(defaultFieldValues.priority),
    dateReceived: defaultFieldValues.dateReceived
      ? dayjs(defaultFieldValues.dateReceived).format('DD MMM YYYY')
      : null,
    fieldValues: pdfFields,
    dateCompleted: dayjs().format('DD MMM YYYY'),
    informed_individuals: modifiedItems,
    has_informed_individuals: hasInformedIndividuals,
    caseManagerReferenceId: caseID,
  };

  return (
    <>
      <Dialog
        onClose={onMinimize}
        open={open}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: '1152px',
            height: '100%',
          },
        }}
        disableRestoreFocus
      >
        <Stack
          direction='row'
          justifyContent='flex-end'
          alignItems='center'
          sx={{
            padding: '4px 8px 4px 20px',
            backgroundColor: '#F6F6F6',
          }}
        >
          <Stack direction='row' spacing={2} alignItems='center'>
            <Button
              variant='text'
              color='darkGray'
              sx={{
                textTransform: 'none',
              }}
              onClick={onMinimize}
              startIcon={<MinimizeIcon />}
            >
              <FormattedMessage defaultMessage='Minimize' />
            </Button>
            <Button
              variant='text'
              color='darkGray'
              sx={{
                textTransform: 'none',
              }}
              onClick={handleClose}
              startIcon={<CloseIcon />}
            >
              <FormattedMessage defaultMessage='Close' />
            </Button>
          </Stack>
        </Stack>
        <DialogActions
          sx={{
            borderTop: '1px solid #D6D6D6',
            paddingX: '32px',
            flexDirection: 'column',
            alignItems: 'baseline',
          }}
        >
          <div className='flex justify-between items-center w-full'>
            <Text>{existingTemplate.template_name}</Text>
            <div className='gap-2 flex items-center flex-1 justify-end'>
              <DownloadFinalReportButton pdfData={pdfData} />
              <NewButton
                variant='plain'
                color='danger'
                onClick={handleOpenWarning}
                disabled={isSubmitting || isSaving}
              >
                <FormattedMessage defaultMessage='Delete' />
              </NewButton>
              <NewButton
                variant='plain'
                color='neutral'
                onClick={handleDiscardChanges}
                disabled={isSubmitting || isSaving}
              >
                <FormattedMessage defaultMessage='Discard changes' />
              </NewButton>
              <NewButton
                variant='outlined'
                color='neutral'
                onClick={handleSaveDraft}
                disabled={isSubmitting || isSaving}
              >
                {isSaving
                  ? intl.formatMessage({ defaultMessage: 'Saving..' })
                  : intl.formatMessage({ defaultMessage: 'Save as draft' })}
              </NewButton>
              <NewButton
                variant='solid'
                color='secondary'
                onClick={handleOpenConfirmation}
                disabled={isSubmitting || isSaving}
              >
                <Text level='body-sm'>
                  {isSubmitting
                    ? intl.formatMessage({ defaultMessage: 'Closing..' })
                    : intl.formatMessage({ defaultMessage: 'Close Report' })}
                </Text>
              </NewButton>
            </div>
          </div>
          <ReportToolbar
            editor={editor}
            activeMarks={activeMarks}
            setMarks={setMarks}
          />
        </DialogActions>
        <Box flexGrow={1} padding='24px' overflow='auto'>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            paddingX='56px'
          >
            <Typography fontSize={24}>
              {existingTemplate.template_title}
            </Typography>
          </Stack>
          <DefaultFields
            reportTypeList={reportTypeList}
            existingValues={defaultFieldValues}
            valueRef={defaultFieldValueRef}
            permissions={permissions}
            checkChanges={handleUnsavedChanges}
          />
          <Box paddingX='56px'>
            <InformedIndividualSectionFactory
              setIsRemove={(isRemove) => {
                setHasInformedIndividuals(isRemove);
              }}
            />
          </Box>

          {CustomFieldSection}
        </Box>
        {errorMessage && (
          <Typography fontSize={12} color='error' marginX='80px' marginY='24px'>
            {errorMessage}
          </Typography>
        )}
        {infoMessage && (
          <Typography fontSize={12} marginX='80px' marginY='24px'>
            {infoMessage}
          </Typography>
        )}
      </Dialog>
      <ConfirmationDialog
        open={openConfirmation}
        onClose={handleCloseConfirmation}
        onSubmit={handleSubmit}
      />
      <SuccessDialog open={openSuccess} onClose={handleCloseSubmission} />
      <UnsavedChangesWarningDialog
        open={blocker.state === 'blocked'}
        onClose={handleCloseUnsavedWarning}
        onConfirm={handleProceedUnsavedWarning}
      />
      <DeleteWarningDialog
        open={openDeleteWarning}
        onClose={handleCloseWarning}
        onConfirm={handleDeleteDraftSubmission}
      />
    </>
  );
};

export default DynamicReportDialog;
